// index 와 순환 import 지만 logger 는 이벤트 시점에만 역참조하므로 안전 (ESM live binding)
import { logger } from './index';

/**
 * 배포 skew 청크 로드 실패 감지 → 1회 강제 새로고침.
 *
 * 배포로 청크 해시가 바뀌면 열려 있던 탭(구 빌드 매니페스트)이 lazy import 하는
 * 구 청크는 어느 pod 에도 없어 404 가 난다. 새 HTML 을 받으면 해결되므로 한 번
 * 새로고침한다. 새로고침 후에도 재발하면(= stale HTML 이 계속 내려오는 캐시/설정
 * 문제) 리로드 루프를 막기 위해 가드 윈도 내에는 재시도하지 않는다.
 */

// webpack(ChunkLoadError)·turbopack·native ESM import 실패의 브라우저별 메시지.
// 번들러/브라우저에 따라 에러 이름이 제각각이라 이름+메시지 패턴으로 판별한다.
const CHUNK_ERROR_PATTERNS = [
  /ChunkLoadError/i,
  /Loading chunk .+ failed/i,
  /Loading CSS chunk/i,
  /Failed to load chunk/i,
  /Failed to fetch dynamically imported module/i, // Chromium
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
];

const RELOAD_AT_KEY = 'vaetki:chunk-reload-at';
const RELOAD_GUARD_WINDOW_MS = 5 * 60 * 1000;

export function isChunkLoadError(reason: unknown): boolean {
  const text =
    reason instanceof Error
      ? `${reason.name}: ${reason.message}`
      : String(reason ?? '');
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * chunk 로드 에러면 강제 새로고침을 예약하고 true 를 반환한다.
 * chunk 에러가 아니거나 가드 윈도 내 재발이면 false (기존 에러 UI 유지).
 */
export function reloadOnChunkError(reason: unknown): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(reason)) {
    return false;
  }

  // sessionStorage 를 못 쓰면(사파리 프라이빗 등) 루프를 가드할 수 없으므로
  // 새로고침을 시도하지 않는다
  try {
    const lastReloadAt = Number(sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
    if (Date.now() - lastReloadAt < RELOAD_GUARD_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
  } catch {
    return false;
  }

  // 리로드로 이 실행 컨텍스트가 사라져도 sendBeacon 이 전송을 보장한다.
  // ELK 에서 이 라인의 스파이크 ↔ 배포 시각 상관으로 skew 노출량을 관측한다.
  logger.error(
    {
      ...(reason instanceof Error ? { err: reason } : {}),
      chunkReload: true,
    },
    '[chunk-reload] stale chunk detected — forcing reload',
  );
  window.location.reload();
  return true;
}
